import { ProductFlowDiagram, type FlowStep } from "@/components/products/ProductFlowDiagram"
import { FAQAccordion, type FAQItem } from "@/components/public/FAQAccordion"
import { LandingFooter } from "@/components/public/LandingFooter"
import { PublicHeader } from "@/components/public/PublicHeader"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { BreathingGradient } from "@/components/ui/living-brand"
import { SpotlightCard } from "@/components/ui/SpotlightCard"
import { Heading } from "@/components/ui/typography"
import { cn } from "@/lib/utils"
import { semanticTokens } from "@supabank/config/theme"
import { Page } from "@supabank/ui"
import { ArrowRight, CheckCircle2, CreditCard, Link2, RefreshCw, Shield } from "lucide-react"
import { useEffect } from "react"
import { Link } from "react-router-dom"

const flowSteps: FlowStep[] = [
  {
    id: "link",
    title: "Link Address to Bank",
    description: "Connect your liquidation address to your bank account. One-time setup, works forever.",
    icon: <Link2 className="h-6 w-6" />,
  },
  {
    id: "deposit",
    title: "Crypto Deposit",
    description: "Send crypto to your liquidation address. We detect the deposit automatically.",
    icon: <CreditCard className="h-6 w-6" />,
  },
  {
    id: "convert",
    title: "Automatic Conversion",
    description: "We convert crypto to fiat at market rates and prepare the payout automatically.",
    icon: <RefreshCw className="h-6 w-6" />,
  },
  {
    id: "payout",
    title: "Bank Deposit",
    description: "Funds arrive in your linked bank account via SEPA, ACH, or Wire—no manual steps.",
    icon: <CheckCircle2 className="h-6 w-6" />,
  },
]

const supportedRails = [
  { name: "SEPA", region: "Eurozone (20+ countries)", speed: "1-2 business days", description: "Single Euro Payments Area - standard or instant SEPA transfers" },
  { name: "ACH", region: "United States", speed: "Next business day", description: "Automated Clearing House - next-day settlement for US domestic transfers" },
  { name: "Wire", region: "Global", speed: "Same day or next day", description: "SWIFT wire transfers - available globally to 100+ countries for USD and EUR" },
  { name: "SPEI", region: "Mexico", speed: "Same day", description: "Sistema de Pagos Electrónicos Interbancarios - Mexico's instant payment system" },
]

const useCases = [
  {
    title: "Automated Settlements",
    description: "Set up once, receive automatic payouts forever. Every crypto deposit converts and sends to your bank automatically.",
  },
  {
    title: "Recurring Payouts",
    description: "Perfect for businesses that receive regular crypto payments. All payouts happen automatically without manual intervention.",
  },
  {
    title: "Merchant Payouts",
    description: "Accept crypto from customers, get fiat in your business account. Fully automated with zero manual steps.",
  },
]

const faqItems: FAQItem[] = [
  {
    id: "what-is",
    q: "What are Payout Routes?",
    a: "Payout Routes link your liquidation address to your bank account. Once configured, every crypto deposit to your address automatically converts to fiat and sends to your linked bank account. It's a one-time setup that works forever.",
  },
  {
    id: "how-setup",
    q: "How do I set up a Payout Route?",
    a: "First, create a liquidation address. Then, link it to your bank account (External Account) during setup. You can link multiple bank accounts and choose which one to use for each address. The setup is one-time and permanent.",
  },
  {
    id: "how-long",
    q: "How long do payouts take?",
    a: "Most payouts complete within 1-2 business days for SEPA/ACH, or same-day for Wire transfers. The exact timing depends on the payment rail you choose and your bank's processing time.",
  },
  {
    id: "fees",
    q: "What are the fees?",
    a: "We charge a small conversion fee (typically 0.5-1%) on the exchange rate, plus a 2% developer fee. Bank transfer fees depend on the payment rail: SEPA is usually free, ACH is free or low-cost, Wire may have fees depending on your bank. You'll see all fees before confirming any transaction.",
  },
  {
    id: "multiple",
    q: "Can I link multiple bank accounts?",
    a: "Yes, you can link multiple bank accounts (External Accounts) and choose which one to use for each liquidation address. Each address can have one linked bank account at a time, but you can change it anytime.",
  },
  {
    id: "security",
    q: "Is it secure?",
    a: "Yes. All payout routes are configured through Bridge API with institutional-grade security. We use KYC verification, automatic compliance checks, and all transactions are monitored. Your bank account details are encrypted and stored securely.",
  },
]

export default function PayoutRoutesPage() {
  useEffect(() => {
    // SEO: Update page title and meta
    document.title = "Automated Payout Routes - Link Crypto Address to Bank Account | Supabank"

    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Link your liquidation address to your bank account. Every crypto deposit automatically converts to fiat and sends to your bank—no manual steps. One-time setup, works forever.')
    }

    // Structured Data (JSON-LD)
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": "Automated Payout Routes",
      "description": "Configuration that links liquidation addresses to bank accounts for automatic crypto-to-fiat conversion and payout",
      "brand": {
        "@type": "Brand",
        "name": "Supabank"
      },
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      }
    }

    const howToData = {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": "How to set up Payout Routes",
      "description": "Step-by-step guide to setting up automated payout routes",
      "step": [
        {
          "@type": "HowToStep",
          "name": "Link Address to Bank",
          "text": "Connect your liquidation address to your bank account. One-time setup, works forever.",
          "position": 1
        },
        {
          "@type": "HowToStep",
          "name": "Crypto Deposit",
          "text": "Send crypto to your liquidation address. We detect the deposit automatically.",
          "position": 2
        },
        {
          "@type": "HowToStep",
          "name": "Automatic Conversion",
          "text": "We convert crypto to fiat at market rates and prepare the payout automatically.",
          "position": 3
        },
        {
          "@type": "HowToStep",
          "name": "Bank Deposit",
          "text": "Funds arrive in your linked bank account via SEPA, ACH, or Wire—no manual steps.",
          "position": 4
        }
      ]
    }

    const faqData = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqItems.map(item => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.a
        }
      }))
    }

    // Remove existing structured data scripts
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]')
    existingScripts.forEach(script => {
      try {
        const data = JSON.parse(script.textContent || '{}')
        if (data['@type'] === 'Product' || data['@type'] === 'HowTo' || data['@type'] === 'FAQPage') {
          script.remove()
        }
      } catch (e) {
        // Ignore parse errors
      }
    })

    // Add new structured data
    const script1 = document.createElement('script')
    script1.type = 'application/ld+json'
    script1.textContent = JSON.stringify(structuredData)
    document.head.appendChild(script1)

    const script2 = document.createElement('script')
    script2.type = 'application/ld+json'
    script2.textContent = JSON.stringify(howToData)
    document.head.appendChild(script2)

    const script3 = document.createElement('script')
    script3.type = 'application/ld+json'
    script3.textContent = JSON.stringify(faqData)
    document.head.appendChild(script3)

    return () => {
      script1.remove()
      script2.remove()
      script3.remove()
    }
  }, [])

  return (
    <Page data-ux-contract="canon" role="main" className="bg-background text-foreground">
      {/* Unified PublicHeader component */}
      <PublicHeader />

      <main className="space-y-0" id="main">
        {/* Hero Section */}
        <section className={cn(
          "relative overflow-hidden",
          "py-12 sm:py-16 md:py-20 lg:py-24",
          // CANON: Hero gradient background (lavenderGlow for fintech feel)
          semanticTokens.heroGradient.lavenderGlow.tailwind,
          // Dark mode variant
          "dark:bg-black dark:bg-[radial-gradient(ellipse_at_20%_50%,hsla(270,95%,75%,0.08)_0%,transparent_50%),radial-gradient(ellipse_at_80%_80%,hsla(217,91%,60%,0.06)_0%,transparent_50%),hsl(var(--background))]"
        )}>
          {/* Living Brand: Breathing Gradient (syncs with LED heartbeat) - DISABLED on dark theme */}
          <BreathingGradient
            variant="lavender"
            syncWithLed
            className="absolute inset-0 z-[1] pointer-events-none opacity-30 dark:hidden"
          />
          <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6">
            <div className="space-y-6 sm:space-y-8 text-center">
              <div className={cn("inline-flex items-center justify-center rounded-full border px-4 py-2 text-sm font-medium", semanticTokens.border.default, semanticTokens.fg.muted, semanticTokens.radius.full)}>
                <Link2 className="mr-2 h-4 w-4" />
                Product
              </div>
              <h1 className={cn("font-display text-display-sm sm:text-display-md md:text-display-lg lg:text-display-lg", semanticTokens.fg.default)}>
                Automated Payout Routes
              </h1>
              <p className={cn("mx-auto max-w-3xl text-lg sm:text-xl leading-relaxed", semanticTokens.fg.muted)}>
                Link your liquidation address to your bank account. Every crypto deposit automatically converts to fiat and sends to your bank—no manual steps. One-time setup, works forever.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                <Button asChild size="lg" className="min-h-[44px] touch-manipulation">
                  <Link to="/waitlist">
                    Set Up Payout Route
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="min-h-[44px] touch-manipulation">
                  <Link to="/how-payments-work">
                    Learn How It Works
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Flow Diagram */}
        <section className={cn(
          "relative overflow-hidden py-12 sm:py-16 md:py-20",
          semanticTokens.harmony.calm.bg
        )}>
          {/* Color Lab: Subtle Blue-Lavender gradient */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `
                radial-gradient(ellipse at 10% 90%, hsla(217, 91%, 60%, 0.10) 0%, transparent 45%),
                radial-gradient(ellipse at 90% 10%, hsla(270, 95%, 75%, 0.08) 0%, transparent 45%)
              `
            }}
          />
          <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6">
            <div className="space-y-8 sm:space-y-10">
              <div className="text-center space-y-3">
                <Heading level={2} className={cn("sm:text-display-sm md:text-display-md", semanticTokens.fg.default)}>
                  How It Works
                </Heading>
                <p className={cn("mx-auto max-w-2xl text-base sm:text-lg", semanticTokens.fg.muted)}>
                  Four simple steps. Fully automated. Zero manual work.
                </p>
              </div>
              <ProductFlowDiagram steps={flowSteps} direction="horizontal" />
            </div>
          </div>
        </section>

        {/* Supported Rails */}
        <section className={cn("py-12 sm:py-16 md:py-20", semanticTokens.bg.surface)}>
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <div className="space-y-8 sm:space-y-10">
              <div className="text-center space-y-3">
                <Heading level={2} className={cn("sm:text-display-sm md:text-display-md", semanticTokens.fg.default)}>
                  Supported Payment Rails
                </Heading>
                <p className={cn("mx-auto max-w-2xl text-base sm:text-lg", semanticTokens.fg.muted)}>
                  Choose the fastest payment rail for your region.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                {supportedRails.map((rail) => (
                  <SpotlightCard
                    key={rail.name}
                    className={cn(
                      "p-5 sm:p-6 border",
                      semanticTokens.bg.surface,
                      semanticTokens.border.default
                    )}
                  >
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h3 className={cn("text-lg font-bold", semanticTokens.fg.default)}>
                          {rail.name}
                        </h3>
                        <Badge variant="outline" className="text-xs">
                          {rail.speed}
                        </Badge>
                      </div>
                      <p className={cn("text-xs font-medium", semanticTokens.fg.muted)}>
                        {rail.region}
                      </p>
                      <p className={cn("text-sm leading-relaxed", semanticTokens.fg.muted)}>
                        {rail.description}
                      </p>
                    </div>
                  </SpotlightCard>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className={cn(
          "relative overflow-hidden py-12 sm:py-16 md:py-20",
          semanticTokens.harmony.calm.bg
        )}>
          {/* Color Lab: Subtle Blue-Lavender gradient */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `
                radial-gradient(ellipse at 10% 90%, hsla(217, 91%, 60%, 0.10) 0%, transparent 45%),
                radial-gradient(ellipse at 90% 10%, hsla(270, 95%, 75%, 0.08) 0%, transparent 45%)
              `
            }}
          />
          <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6">
            <div className="space-y-8 sm:space-y-10">
              <div className="text-center space-y-3">
                <Heading level={2} className={cn("sm:text-display-sm md:text-display-md", semanticTokens.fg.default)}>
                  Use Cases
                </Heading>
                <p className={cn("mx-auto max-w-2xl text-base sm:text-lg", semanticTokens.fg.muted)}>
                  Perfect for businesses and individuals who receive regular crypto payments.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
                {useCases.map((useCase) => (
                  <SpotlightCard
                    key={useCase.title}
                    className={cn(
                      "p-6 sm:p-8 border",
                      semanticTokens.bg.surface,
                      semanticTokens.border.default
                    )}
                  >
                    <div className="space-y-3">
                      <h3 className={cn("text-lg font-bold", semanticTokens.fg.default)}>
                        {useCase.title}
                      </h3>
                      <p className={cn("text-sm leading-relaxed", semanticTokens.fg.muted)}>
                        {useCase.description}
                      </p>
                    </div>
                  </SpotlightCard>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Security & Compliance */}
        <section className={cn("py-12 sm:py-16 md:py-20", semanticTokens.bg.surface)}>
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <div className="space-y-6 sm:space-y-8">
              <div className="text-center space-y-3">
                <Heading level={2} className={cn("sm:text-display-sm md:text-display-md", semanticTokens.fg.default)}>
                  Security & Compliance
                </Heading>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
                <SpotlightCard className={cn("p-6 sm:p-8 border", semanticTokens.bg.surface, semanticTokens.border.default)}>
                  <div className="flex items-start gap-4">
                    <div className={cn("rounded-lg p-3", semanticTokens.bg.muted)}>
                      <Shield className={cn("h-6 w-6", semanticTokens.fg.default)} />
                    </div>
                    <div className="space-y-2 flex-1">
                      <h3 className={cn("text-lg font-bold", semanticTokens.fg.default)}>
                        Institutional Security
                      </h3>
                      <p className={cn("text-sm leading-relaxed", semanticTokens.fg.muted)}>
                        All payout routes are configured through Bridge API with institutional-grade security. Your bank account details are encrypted and stored securely.
                      </p>
                    </div>
                  </div>
                </SpotlightCard>

                <SpotlightCard className={cn("p-6 sm:p-8 border", semanticTokens.bg.surface, semanticTokens.border.default)}>
                  <div className="flex items-start gap-4">
                    <div className={cn("rounded-lg p-3", semanticTokens.bg.muted)}>
                      <CheckCircle2 className={cn("h-6 w-6", semanticTokens.fg.default)} />
                    </div>
                    <div className="space-y-2 flex-1">
                      <h3 className={cn("text-lg font-bold", semanticTokens.fg.default)}>
                        Automatic Compliance
                      </h3>
                      <p className={cn("text-sm leading-relaxed", semanticTokens.fg.muted)}>
                        KYC verification, automatic compliance checks, and transaction monitoring. All transactions are screened before funds move.
                      </p>
                    </div>
                  </div>
                </SpotlightCard>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className={cn(
          "relative overflow-hidden py-12 sm:py-16 md:py-20",
          semanticTokens.harmony.calm.bg
        )}>
          {/* Color Lab: Subtle Lavender gradient */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `
                radial-gradient(ellipse at 80% 20%, hsla(270, 95%, 75%, 0.08) 0%, transparent 45%),
                radial-gradient(ellipse at 20% 80%, hsla(217, 91%, 60%, 0.06) 0%, transparent 45%)
              `
            }}
          />
          <div className="relative z-10 mx-auto max-w-4xl px-4 sm:px-6">
            <div className="space-y-8 sm:space-y-10">
              <div className="text-center space-y-3">
                <Heading level={2} className={cn("sm:text-display-sm md:text-display-md", semanticTokens.fg.default)}>
                  Frequently Asked Questions
                </Heading>
              </div>
              <FAQAccordion items={faqItems} />
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className={cn("py-12 sm:py-16 md:py-20", semanticTokens.bg.surface)}>
          <div className="mx-auto max-w-4xl px-4 sm:px-6 text-center space-y-6">
            <Heading level={2} className={cn("sm:text-display-sm md:text-display-md", semanticTokens.fg.default)}>
              Ready to Get Started?
            </Heading>
            <p className={cn("text-base sm:text-lg", semanticTokens.fg.muted)}>
              Set up your payout route and start receiving automatic bank deposits today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="min-h-[44px] touch-manipulation">
                <Link to="/waitlist">
                  Set Up Payout Route
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="min-h-[44px] touch-manipulation">
                <Link to="/products">
                  View All Products
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <LandingFooter />
    </Page>
  )
}

